import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { CSSProperties } from 'react';
import { Button, Divider } from 'antd';
import { useDevice, useThemeToken } from '@/hooks';
import {
  Flex,
  Swiper,
  SwiperSlide,
  SwiperProps,
  Mousewheel,
  Navigation,
  Pagination,
  EffectCards,
  getDefaultNavigation,
  LoadingSpin,
} from '../common';
import { envConfig } from '@/configs';
import { parseError } from '@/utils';
import { textColor, getImage, swiperEmptyImage } from './func';

import './styles.scss';

export interface SwiperItemAttrs {
  id: number;
  disabled?: boolean;
  count?: number;
  title?: string | React.ReactElement;
  titleClassname?: string;
  subTitle?: string | React.ReactElement;
  description?: string | React.ReactElement;
  secondaryDescription?: string | React.ReactElement;
  actionButtonContent?: any;
  data?: any;
  foodImageSrc?: string;
  startServeTime?: string;
  endServeTime?: string;
  demonstrationDescription?:string;
}

export interface SwiperConfigableProps extends SwiperProps {
  id?: string;
  card?: boolean;
  layout?: 'row' | 'column';
  buttonGrow?: boolean;
  items?: SwiperItemAttrs[];
  style?: CSSProperties;
  direction?: 'vertical' | 'horizontal';
  onActionClick?: (item: SwiperItemAttrs) => void;
  scale?: boolean;
  compact?: boolean;
  variant?: ComponentVariant;
  loading?: boolean;
  error?: string;
  smallButton?: boolean;
}

export const SwiperConfigable: React.FC<SwiperConfigableProps> = ({
  id = 'swiper-configable',
  variant = 'nurture',
  items = [],
  card = true,
  error = null,
  layout = 'row',
  loading = false,
  scale = false,
  buttonGrow = false,
  style = {},
  direction = 'vertical',
  compact = false,
  smallButton = false,
  onActionClick = (_item: SwiperItemAttrs) => { },
  ...rest
}) => {



  const { colorPrimary } = useThemeToken();
  const { isDesktop } = useDevice();
  const isRow = layout === 'row';
  const modules = [Navigation, EffectCards, Mousewheel];

  const swiperClasses = classNames(
    'app-swiper-enhanced w-full',
    `app-swiper-theme-${variant}`,
    { 'app-swiper-enhanced-scale': scale },
    { 'app-swiper-enhanced-compact': compact },
    { 'app-swiper-enhanced-card': card },
    { 'app-swiper-enhanced-row': layout === 'row' },
    { 'app-swiper-enhanced-column': layout === 'column' },
  );

  const actionButtonClasses = classNames(
    'swiper-action-button',
    direction === 'horizontal' ? 'text-sm px-3 !w-auto' : '',
    buttonGrow ? 'w-full' : 'ms-auto min-w-28',
  );

  // Show pagination if foods count is equal or less than 5
  if (items.length <= 7) {
    modules.push(Pagination);
  }

  const paginationOptions = items.length <= 7 ? {
    pagination: { clickable: true },
  } : {};

  const textClass = textColor(variant);
  const [t] = useTranslation(['common', 'report', 'reserve']);
  return (
    <Swiper
      // autoHeight
      direction={direction}
      slidesPerView={1}
      spaceBetween={20}
      mousewheel={true}
      centeredSlides={true}
      modules={modules}
      className={swiperClasses}
      style={{ height: 400, minHeight: 175, marginBlock: '2rem', ...style }}
      scrollbar={{ draggable: true, enabled: true }}
      navigation={getDefaultNavigation(id)}
      {...paginationOptions}
      {...(card ? { effect: 'cards' } : {})}
      {...rest}
    >
      {(error || items.length === 0) && (
        <SwiperSlide style={{ width: '100%' }}>
          <Flex gap={16} className="w-full h-full" direction="column" justify="center" align="center">
            {loading ? <LoadingSpin className={`text-${variant}-darker text-base`} /> : (
              <b className="text-[#979797] py-2">
                {error ? parseError(error) : (
                  <Flex direction="column" justify="center" align="center">
                    <Flex className={`${isDesktop() ? '' : 'max-h-[110px]'} pt-2`} inlineFlex>
                      {swiperEmptyImage(variant)}
                    </Flex>
                    <span className="pt-2">{t(`common:messages.main-swiper-no-items.${variant}`)}</span>
                  </Flex>
                )}
              </b>
            )}
          </Flex>
        </SwiperSlide>
      )}
      {items.map((item, index) => (
        <SwiperSlide key={index}
          className={classNames({ 'slide-disabled': item.disabled })}>
          {item.count && <div className="app-badge-count">{item.count}</div>}
          <Flex className="app-food-slide-content" direction="column">
            <Flex
              align={isRow ? 'center' : 'flex-start'}
              justify={isRow ? 'flex-start' : 'flex-end'}
              direction={isRow ? 'row' : 'column'}
            >
              {isDesktop() &&
                <img
                  alt="Image"
                  src={item.foodImageSrc ? item?.foodImageSrc : getImage(variant)}
                  className={classNames(
                    'swiper-img pt-1',
                    item?.foodImageSrc != null ? 'aspect-square rounded-full object-cover object-center' : ''
                  )}
                  style={{
                    width: item?.foodImageSrc ? '120px' : undefined,
                    height: item?.foodImageSrc ? '120px' : undefined,
                    border: item?.foodImageSrc ? `2px solid ${colorPrimary}` : undefined,
                    borderRadius: ['dormitory', 'helpdesk'].includes(variant) ? '0' : '50%',
                    filter: envConfig?.ui?.swiperShadow ? 'drop-shadow(rgba(0, 0, 0, 0.25) 5px 5px 5px)' : undefined
                  }}
                />
              }
              <Flex direction="column" className="pt-2 text-sm lg:text-base w-full" align="flex-start">
                <b className={classNames(`${textClass} !text-start`, item.titleClassname)}>{item.title}</b>
                <b className={`${textClass} !text-start`}>{item.subTitle}</b>
                {(item.startServeTime && envConfig?.ui?.showLessReserveDetail === false) &&
                <Flex direction="row" className="pt-3 !text-start text-[#979797] text-xs font-bold">
                  <span>
                  زمان شروع :
                    {item?.startServeTime}
                  </span>
                  <span>
                    زمان پایان :
                    {item?.endServeTime}
                  </span>
                </Flex>}
                {item.demonstrationDescription &&
                  <Flex direction="row" className="!text-start text-[#979797] text-xs font-bold">
                    <span>
                      {item.demonstrationDescription}
                    </span>
                  </Flex>
                }
              </Flex>
            </Flex>
            {direction === 'vertical' && <Divider className="mt-2 mb-0" />}
            <Flex direction="column" className="py-2 !text-start justify-between">
                <b className="text-base lg:text-lg text-justify line-clamp-2">
                  {variant === 'health' ?
                    t(`report:activity-report-activity-fields.foodNames.${variant}`) :
                    ''
                  }
                  {item.description}
                </b>
                <div className="text-[#979797] font-bold">{item.secondaryDescription}</div>
              </Flex>


            {item.actionButtonContent &&
              <Button size={smallButton ? 'small' : 'middle'} className={actionButtonClasses} onClick={() => onActionClick(item)}>
                {item.actionButtonContent}
              </Button>
            }
          </Flex>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
